import React, { memo, ReactNode, HTMLAttributes, ElementType } from 'react';
import clsx from 'clsx';

import theme from '@smart-tv/themes/theme';
import styles from './typography.module.scss';

const colorMapper: Record<string, string> = {
  initial: 'typography--color-initial',
  inherit: 'typography--color-inherit',
  primary: 'typography--color-primary',
  textPrimary: 'typography--color-text-primary',
  textSecondary: 'typography--color-text-secondary',
  error: 'typography--color-error',
  errorSecondary: 'typography--color-error-secondary',
  label: 'typography--color-label',
  labelTint: 'typography--color-label-tint'
};

const variantClassMapper: Record<string, string> = {
  h1: 'typography--h1',
  h2: 'typography--h2',
  h3: 'typography--h3',
  h4: 'typography--h4',
  h5: 'typography--h5',
  h6: 'typography--h6',
  body: 'typography--body',
  bodySmall: 'typography--body-small',
  caption: 'typography--caption',
  caption2: 'typography--caption2'
};

const variantTagMapper: Record<string, string> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  body: 'p',
  bodySmall: 'p',
  caption: 'span',
  caption2: 'span'
};

export interface Props {
  children: ReactNode | ReactNode[];
  fontWeight?: 'light' | 'regular' | 'medium' | 'bold';
  color?:
    | 'initial'
    | 'inherit'
    | 'primary'
    | 'textPrimary'
    | 'textSecondary'
    | 'error'
    | 'errorSecondary'
    | 'label'
    | 'labelTint';
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body' | 'bodySmall' | 'caption' | 'caption2';
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  textTransform?: 'uppercase' | 'lowercase' | 'capitalize' | 'none' | 'initial' | 'inherit';
  noWrap?: boolean;
  gutterBottom?: number;
  gutterTop?: number;
  gutter?: number;
  component?: ElementType;
}

const Typography = (props: Props & HTMLAttributes<HTMLElement>) => {
  const {
    children,
    fontWeight,
    color,
    textAlign,
    variant = 'body',
    component,
    noWrap,
    textTransform,
    gutter,
    gutterTop,
    gutterBottom,
    className,
    style,
    ...rest
  } = props;

  const tag: ElementType = component || variantTagMapper[variant] || 'span';

  // Build inline styles for dynamic gutter values
  const gutterStyle = {
    ...(gutter && { margin: `${theme.spacing(gutter)} 0` }),
    ...(gutterTop && { marginTop: theme.spacing(gutterTop) }),
    ...(gutterBottom && { marginBottom: theme.spacing(gutterBottom) }),
    ...style
  };

  const hasGutter = gutter || gutterTop || gutterBottom;

  // Build className with all modifiers
  const computedClassName = clsx(
    styles.typography,
    variant && styles[variantClassMapper[variant]],
    fontWeight && styles[`typography--weight-${fontWeight}`],
    color && styles[colorMapper[color]],
    textAlign && styles[`typography--align-${textAlign}`],
    textTransform && styles[`typography--transform-${textTransform}`],
    noWrap && styles['typography--no-wrap'],
    hasGutter && styles['typography--gutter-block'],
    className
  );

  return React.createElement(
    tag,
    { className: computedClassName, style: gutterStyle, ...rest },
    children
  );
};

export default memo(Typography);
